// src/components/VideoFeedbackKaart.js
//
// Korte vragenlijst onder de samenvatting van een video-analyse (pilot videocoaching): bruikbaarheid
// en kloppen van de feedback (inhoud), hoe filmen en uploaden ging (uitvoering) en een open vraag.
// Eén antwoord per analyse, later te wijzigen. "Later" verbergt de kaart tot het volgende bezoek.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { videoHeaders } from '../utils/videoVerificatie';
import './VideoFeedbackKaart.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const OPMERKING_MAX = 1000;
const LEEG = { bruikbaarheid: null, klopt: null, uitvoering: null, opmerking: '' };
const SCORES = [1, 2, 3, 4, 5].map((n) => ({ waarde: n, label: String(n) }));
const KLOPT = [
  { waarde: 'ja', label: 'Ja' },
  { waarde: 'deels', label: 'Deels' },
  { waarde: 'nee', label: 'Nee' },
];

function Keuze({ naam, vraag, uitleg, opties, waarde, onKies }) {
  const uitlegId = uitleg ? `${naam}-uitleg` : undefined;
  return (
    <fieldset className="video-feedback__vraag" aria-describedby={uitlegId}>
      <legend>{vraag}</legend>
      {uitleg && <p id={uitlegId} className="video-feedback__uitleg">{uitleg}</p>}
      <div className="video-feedback__opties">
        {opties.map((o) => (
          <label key={o.waarde} className="video-feedback__optie">
            <input
              type="radio"
              name={naam}
              value={o.waarde}
              checked={waarde === o.waarde}
              onChange={() => onKies(o.waarde)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function VideoFeedbackKaart({ analyseId }) {
  // laden | vragen | bedankt | dicht
  const [stand, setStand] = useState('laden');
  const [antwoorden, setAntwoorden] = useState(LEEG);
  // Het bewaarde antwoord (null: nog niet beantwoord); "Annuleren" zet dat terug.
  const [opgeslagen, setOpgeslagen] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const bedanktRef = useRef(null);

  useEffect(() => {
    let actief = true;
    axios
      .get(`${STRAPI_URL}/api/video-feedbacks/${analyseId}`, { headers: videoHeaders(localStorage.getItem('jwt')) })
      .then((res) => {
        if (!actief) return;
        const bestaand = res.data?.data;
        if (bestaand) {
          const bewaard = { ...LEEG, ...bestaand, opmerking: bestaand.opmerking || '' };
          setAntwoorden(bewaard);
          setOpgeslagen(bewaard);
          setStand('bedankt');
        } else {
          setStand('vragen');
        }
      })
      .catch(() => actief && setStand('dicht'));
    return () => {
      actief = false;
    };
  }, [analyseId]);

  const zet = (veld) => (waarde) => setAntwoorden((a) => ({ ...a, [veld]: waarde }));

  const verstuur = async (e) => {
    e.preventDefault();
    const { bruikbaarheid, klopt, uitvoering, opmerking } = antwoorden;
    if (!bruikbaarheid || !klopt || !uitvoering) {
      setFout('Kies bij de drie vragen een antwoord. De open vraag mag je overslaan.');
      return;
    }
    setBezig(true);
    setFout(null);
    try {
      await axios.put(
        `${STRAPI_URL}/api/video-feedbacks/${analyseId}`,
        { bruikbaarheid, klopt, uitvoering, opmerking },
        { headers: videoHeaders(localStorage.getItem('jwt')) }
      );
      setOpgeslagen(antwoorden);
      setStand('bedankt');
    } catch {
      setFout('Versturen lukte niet. Probeer het opnieuw.');
    } finally {
      setBezig(false);
    }
  };

  useEffect(() => {
    if (stand === 'bedankt' && !bezig) bedanktRef.current?.focus();
  }, [stand, bezig]);

  if (stand === 'laden' || stand === 'dicht') return null;

  if (stand === 'bedankt') {
    return (
      <section className="video-feedback video-feedback--bedankt" aria-label="Feedback op deze analyse">
        <p ref={bedanktRef} tabIndex={-1} role="status" className="video-feedback__bedankt">
          <strong>Bedankt voor je feedback.</strong> Je antwoorden helpen ons de video-analyse beter te maken.
        </p>
        <button type="button" className="video-feedback__link" onClick={() => setStand('vragen')}>
          Antwoorden wijzigen
        </button>
      </section>
    );
  }

  return (
    <section className="video-feedback" aria-labelledby="video-feedback-titel">
      <h2 id="video-feedback-titel" className="video-feedback__titel">Hoe was dit?</h2>
      <p className="video-feedback__intro">Drie korte vragen over deze analyse. Het kost je een halve minuut.</p>
      <form onSubmit={verstuur} noValidate>
        <p className="video-feedback__groep">Inhoud</p>
        <Keuze
          naam="bruikbaarheid"
          vraag="Hoe bruikbaar is deze feedback voor je volgende les?"
          uitleg="1 = niet bruikbaar, 5 = heel bruikbaar"
          opties={SCORES}
          waarde={antwoorden.bruikbaarheid}
          onKies={zet('bruikbaarheid')}
        />
        <Keuze
          naam="klopt"
          vraag="Klopt de feedback met wat er in je les gebeurde?"
          opties={KLOPT}
          waarde={antwoorden.klopt}
          onKies={zet('klopt')}
        />
        <p className="video-feedback__groep">Uitvoering</p>
        <Keuze
          naam="uitvoering"
          vraag="Hoe ging het filmen en uploaden?"
          uitleg="1 = heel moeizaam, 5 = heel makkelijk"
          opties={SCORES}
          waarde={antwoorden.uitvoering}
          onKies={zet('uitvoering')}
        />
        <div className="video-feedback__vraag">
          <label htmlFor="video-feedback-opmerking">
            Wat mis je, of wat kan beter? <span className="video-feedback__optioneel">(optioneel)</span>
          </label>
          <textarea
            id="video-feedback-opmerking"
            rows={3}
            maxLength={OPMERKING_MAX}
            value={antwoorden.opmerking}
            onChange={(e) => zet('opmerking')(e.target.value)}
            aria-describedby="video-feedback-teller"
          />
          <p id="video-feedback-teller" className="video-feedback__teller">
            {antwoorden.opmerking.length} / {OPMERKING_MAX}
          </p>
        </div>
        {fout && <p className="video-feedback__fout" role="alert">{fout}</p>}
        <div className="video-feedback__knoppen">
          <button type="submit" className="video-feedback__verstuur" disabled={bezig}>
            {bezig ? 'Versturen...' : opgeslagen ? 'Wijziging opslaan' : 'Versturen'}
          </button>
          <button
            type="button"
            className="video-feedback__link"
            onClick={() => {
              if (!opgeslagen) return setStand('dicht');
              setAntwoorden(opgeslagen);
              setFout(null);
              setStand('bedankt');
            }}
          >
            {opgeslagen ? 'Annuleren' : 'Later'}
          </button>
        </div>
      </form>
    </section>
  );
}
